import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useImmer } from "use-immer";
import AppContext from "../../utils/AppContext";
import { userFullName } from "../../utils/userUtils";
import { handleNetworkError } from "../../utils/api";
import { useDebouncedCallback } from "use-debounce";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { DateTime } from "luxon";

import useGlobalParams from "../../hooks/useGlobalParams";
import useUserRoles from "../../hooks/useUserRoles";
import useUsers from "../../hooks/useUsers";
import useBusinesses from "../../hooks/useBusinesses";
import useMutateRole from "../../hooks/useMutateRole";
import useToast from "../../hooks/useToast";

import PLink from "../../common/Navigation/PLink";
import UserRoleDropdown from "./UserRoleDropdown";
import Button from "../../common/Button/Button";
import CategoriesSelector from "./CategoriesSelector";
import BusinessSelector from "./BusinessSelector";
import InvitationModal from "./InvitationModal";
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Toast from "../../common/Toast/Toast";
import OTPModal from "../../common/OTPModal/OTPModal";
import ConfirmationModal from "../../common/ConfirmationModal/ConfirmationModal";

import { ReactComponent as ErrorIcon } from "../../assets/icons_svg/error-lined.svg";

import './UserDetails.scss';

const NEW_USER = {
  business_ids     : [],
  category_ids     : [],
  invitation_email : '',
  role             : null,
}

export default function UserDetails({ newUser }) {
  const { organizationId } = useGlobalParams();
  const { roleId: paramRoleId } = useParams();
  const location = useLocation();
  const roleId = parseInt(paramRoleId, 10);
  const navigate = useNavigate();

  const [state, dispatch] = useContext(AppContext);
  const { currentUser } = state;

  const { t } = useTranslation();
  
  const { data: users } = useUsers({ organizationId });
  const { data: userRoles } = useUserRoles({ organizationId });
  const { data: businesses } = useBusinesses({ organization_id: organizationId });
  
  const { createInvitation, updateUserRole, deleteUserRole, resendInvitation } = useMutateRole({ organizationId });
  
  const [editedRole, setEditedRole] = useImmer(() => newUser ? NEW_USER : null);
  const [categoryBusiness, setCategoryBusiness] = useImmer([]);
  const [errors, setErrors] = useState(false);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roleModified, setRoleModified] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [allowAssign, setAllowAssign] = useState(false);
  const [showInvited, setShowInvited] = useState(false);
  const [emailChanged, setEmailChanged] = useState(false);
  const [invitationExpired, setInvitationExpired] = useState(false);
  const [resending, setResending] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [afterVerify, setAfterVerify] = useState(null);
  const [showDemote, setShowDemote] = useState(false);

  const emailRef = useRef();

  const { toast, showToast } = useToast();
  
  const userRole = userRoles?.find(role => role.id === roleId);
  const user = users?.find(user => user.id === userRole?.user_id);

  useEffect(() => {
    console.log('errors:', errors)
    if (errors?.invitation_email) {
      emailRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [errors]);

  useEffect(() => {
    if (userRole && !roleModified) {
      if ((userRole.type === 'admin' || userRole.type === 'business')) {
        setEditedRole(userRole);

        if (userRole.category_ids?.length > 0) {
          setAllowAssign(true);
        }
      }
      else {
        setEditedRole(userRole);
      }

      (userRole.type === 'category' || userRole.type === 'lead') && setCategoryBusiness(userRole.business_ids);
      setRoleModified(false);

      const expiryDate = DateTime.fromISO(userRole.invitation_expiration);

      if (DateTime.now().startOf('day') >  expiryDate.startOf('day')) {
        setInvitationExpired(true);
      }
    }
  }, [userRole, roleModified, setEditedRole, setCategoryBusiness]);

  const debounceHideLinkCopied = useDebouncedCallback(() => {
    setLinkCopied(false);
  }, [2000]);

  if (!users || !userRoles || !businesses) {
    return;
  }

  // going to an unexisting user id (IE: /user-roles/-1/)
  if (!newUser && !userRole) {
    navigate({ pathname: '/users', search: location.search }, { replace: true });
    return;
  }

  const changeRole = newRole => {
    setEditedRole(draft => {
      draft.type = newRole;
    });

    setRoleModified(true);
  }

  const changeBusinesses = (id, checked) => {
    setEditedRole(draft => {
      if (checked) {
        draft.business_ids.push(id);
      }
      else {
        const index = draft.business_ids.findIndex(businessId => businessId === id);
        draft.business_ids.splice(index, 1);
      }
    });

    setRoleModified(true);
  }

  const onDeleteRole = () => {
    const onSuccess = () => {
      dispatch({ type: 'globalToast/set', payload: { message: "User deleted from organization" }});
      navigate({ pathname: '/users', search: location.search }, { replace: true });
    }

    const onError = e => {
      checkForVerification(e, 'delete');
    }

    deleteUserRole.mutate(roleId, { onSuccess, onError });
  }

  const checkForVerification = (e, afterAction) => {
    const code = e?.response?.data?.code;

    if (code === 'verification_required') {
      setShowOTP(true);
      setAfterVerify(afterAction);
    }

    else {
      handleNetworkError({ e, setErrors, setValidated });
    }
  }

  const onSaveRole = (e, verified) => {
    setShowDemote(false);

    // TODO -> validation a lo pnegro
    if (((editedRole.type === 'category' || editedRole.type === 'lead') || allowAssign) && editedRole.category_ids.length === 0) {
      alert("Select at least one category for this user");
      return;
    }
    else if (editedRole.type === 'business' && editedRole.business_ids.length === 0) {
      alert("Select at least one business for this user");
      return;
    }

    // When demoting, make sure user exists (not just an invitation):
    if (!verified && user && currentUser.id === user.id && editedRole.type !== 'admin') {
      setAfterVerify('save');
      setShowDemote(true);
      return;
    }

    const data = {
      business_ids : editedRole.business_ids,
      category_ids : editedRole.category_ids,
      type         : editedRole.type,
    }

    if (editedRole.type === 'category' || editedRole.type === 'lead' || allowAssign) {
      data.business_ids = categoryBusiness;
    }
    else if (editedRole.type === 'admin') {
      data.business_ids = [];
      data.category_ids = [];
    }

    if (!user && editedRole.invitation_email !== userRole.invitation_email) {
      data.invitation_email = editedRole.invitation_email;
    }

    const onError = e => {
      checkForVerification(e, 'save');
    }

    const onSuccess = data => {
      setRoleModified(false);

      if (!user && editedRole.invitation_email !== userRole.invitation_email) {
        setShowInvited(true);
        setEmailChanged(true);
      }
      else {
        alert("ROLE SAVED 🔥");
      }

      setEditedRole(data);
    }

    updateUserRole.mutate({ id: userRole.id, data }, { onSuccess, onError });
  }

  const onResend = async () => {
    const data = { invitation_email: userRole.invitation_email };

    const onSuccess = () => {
      setInvitationExpired(false);
      setResending(false);
      showToast("Invitation resent to " + userRole.invitation_email);
    }

    setResending(true);
    resendInvitation.mutate({ id: userRole.id, data }, { onSuccess });
  }

  const createUser = async () => {
    if ((editedRole.type === 'category' || editedRole.type === 'lead') && editedRole.category_ids.length === 0) {
      alert("Select at least one category for this user");
      return;
    }
    else if (editedRole.type === 'business' && editedRole.business_ids.length === 0) {
      alert("Select at least one business for this user");
      return;
    }

    setLoading(true);
    setErrors(null);
    setValidated(false);
    
    const data = { organization_id: organizationId, ...editedRole };

    if (editedRole.type === 'category' || editedRole.type === 'lead') {
      data.business_ids = categoryBusiness;
    }
    else if (editedRole.type === 'admin') {
      data.business_ids = [];
      data.category_ids = [];
    }
    else if (editedRole.type === 'business') {
      data.category_ids = [];
    }

    const onSettled = () => setLoading(false);
    const onError = e => checkForVerification(e, 'create');
    
    const onSuccess = data => {
      setShowInvited(true);
      setEditedRole(data);
    }

    createInvitation.mutate({ data, organizationId }, { onError, onSettled, onSuccess });
  }

  const onConfirmOTP = () => {
    if (afterVerify === 'create') {
      createUser();
    }
    else if (afterVerify === 'save') {
      onSaveRole(null, true);
    }
    else if (afterVerify === 'delete') {
      onDeleteRole();
    }

    setShowOTP(false);
  }

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/invite?token=${userRole.invitation_token}`);
    
    setLinkCopied(true);
    debounceHideLinkCopied();
  }

  const changeAllowAssign = e => {
    setAllowAssign(e.target.checked);
    setRoleModified(true);
  }

  const onChangeEmail = e => {
    setEditedRole(draft => { draft.invitation_email = e.target.value });
    setRoleModified(true);
  }

  const renderRoleDescription = () => {
    switch (editedRole.type) {
      case 'admin':
        return <p>This user can fully manage the organization, including billing, editing or creating new businesses, adding new users, and editing or adding users. <a href="#sup">Learn more.</a></p>;

      case 'business':
        return <><p>This user can modify only, selected businesses, create and edit categories, and create, delete and edit leads. <a href="#sup">Learn more.</a></p></>;

      case 'category':
        return <p>This user can edit selected categories, and create, edit, and delete leads in these categories. <a href="#sup">Learn more.</a></p>;

      case 'lead':
        return <p>This user can only view, delete and edit users that are assigned to them. The user won't be able to see other users or leads. <a href="#sup">Learn more.</a></p>;

      default:
        return <p>Select a role for this user. To learn more about the different roles, check our <a href="#hodor">User & Roles Documentation</a>.</p>
    }
  }

  const renderSelectedAssets = () => {
    const categoryCount = editedRole.category_ids.length;
    const businessCount = categoryBusiness.length;

    if (categoryCount > 0) {
      return <p className="category-count">You have <strong>{ categoryCount } {categoryCount === 1 ? 'category' : 'categories' }</strong> selected in <strong>{ businessCount === 1 ? 'one business' : `${businessCount} businesses` }</strong></p>
    }
  }

  if (!editedRole) {
    return;
  }

  return (
    <section className='__user-details'>
      <div className={classNames("alert-bar", { show: roleModified && !newUser })}>
        <span>You have unsaved changes for this user</span>
        <button className="action" onClick={onSaveRole}>Save Changes</button>
      </div>
      <div className="details-content">
        <div className="title-nav">
          <h2>{ newUser ? "Add New User" : "Edit Role" }</h2>
          <PLink to='/users'>« Back</PLink>
        </div>

        { invitationExpired && (
          <div className="invitation-expired">
            <ErrorIcon />
            <div className="content">This invitation expired on {DateTime.fromISO(userRole.invitation_expiration).toLocaleString(DateTime.DATE_FULL)}</div>
            { resending
            ? <Spinner as="span" animation="border" size="sm" />
            :
            <>
              <Button variant='link' onClick={onResend}>Resend</Button>
              <Button variant='link' onClick={onDeleteRole}>Delete</Button>
            </>
            }
          </div>
        )}
        
        { user && (
          <>
            <Form.Group className="label-field">
              <Form.Label>Name</Form.Label>
              <div className="field-error">
                <Form.Control disabled={user} type='text' value={userFullName(user)} />
              </div>
            </Form.Group>
          </>
        )}

        <>
          <Form.Group className="label-field" ref={emailRef}>
            <Form.Label>Email address</Form.Label>
            <div className="field-error">
              <Form.Control disabled={user || invitationExpired} type='email' value={user?.email || editedRole.invitation_email} onChange={onChangeEmail} isValid={validated && !errors?.invitation_email} isInvalid={errors?.invitation_email} />
              { errors?.invitation_email?.map(error => <Form.Control.Feedback type='invalid' key={error}>{ error } </Form.Control.Feedback>)}
            </div>
          </Form.Group>
          { !user && editedRole && editedRole.invitation_email !== userRole?.invitation_email && <div className="input-details new-link">A new invitation link will be sent to the new email and the previous one will be invalidated.</div> }
          { user?.email && <div className="input-details">To update a user's name or email, the user must log in to their account and update via Settings &gt; Profile.</div> }
          { newUser && <div className="input-details">An invitation will be sent to this email address. This invitation expires in 90 days.</div> }
        </>

        { !user && !newUser && (
          <div className="invite-link">
            { linkCopied
            ? <div className="url-copied">URL copied to clipboard</div>
            : <div className="invite-min-height" />
            }
            <div className="link-field">
              <div className="label">Invitation link</div>
              <Form.Control value={`${window.location.origin}/invite?token=${userRole?.invitation_token}`} readOnly disabled={invitationExpired} />
              <button className="copy" onClick={handleCopyLink} disabled={invitationExpired}>{ linkCopied ? 'Copied' : 'Copy' }</button>
            </div>
          </div>
        )}

        <>
          <div className="label-field">
            <div className="form-label">User Type</div>
            <div className="field-error">
              <UserRoleDropdown disabled={invitationExpired} onChange={changeRole} role={editedRole.type} placeholder={t('Select a role')} type='large' />
            </div>
          </div>
          { <div className="input-details">{ renderRoleDescription() }</div> }
        </>

        { editedRole?.type === 'business' && (
          <>
            <h2 className="selector">Businesses</h2>
            <p className="select-description">Select the businesses that this user will have access to:</p>
            <BusinessSelector disabled={invitationExpired} businesses={businesses} onChange={changeBusinesses} role={editedRole} />
          </>
        )}

        { ['admin', 'business'].includes(editedRole?.type) && (
          <>
            <Form.Check disabled={invitationExpired} type="switch" className={classNames('allow-assign', { 'from-business': editedRole.type === 'business' })} onChange={changeAllowAssign} checked={allowAssign} label='Allow leads to be assigned to this user' />
            <div className={classNames("input-details", { 'from-business': editedRole.type === 'business' })}>If checked, this user will appear in the list of users who can have leads assigned to them.</div>
          </>
        )}

        { (['category', 'lead'].includes(editedRole?.type) || allowAssign) && (
          <>
            <h2 className="selector">Categories</h2>
            <p className="select-description">Select the categories that this user can have leads assigned to:</p>
            <CategoriesSelector disabled={invitationExpired} businesses={editedRole.type === 'business' ? businesses.filter(business => editedRole.business_ids.includes(business.id)) : businesses} role={editedRole} setEditedRole={setEditedRole} onChange={() => setRoleModified(true)} setCategoryBusiness={setCategoryBusiness} />
            { renderSelectedAssets() }
          </>
        )}

        <div className="nav-bottom">
          { !newUser 
          ? <>
              <Button variant='primary' onClick={onSaveRole} disabled={!roleModified}>Save Changes</Button>
              { currentUser.id !== user?.id && <Button className='delete-user' onClick={onDeleteRole}>Delete User</Button> }
            </>
          : <Button className='m-2' variant='primary' disabled={!editedRole.type && !editedRole.email} onClick={createUser} loading={loading}>Send Invitation</Button>
        }
        </div>
      </div>

      <InvitationModal show={showInvited} role={editedRole} emailChanged={emailChanged} onHide={() => navigate({ pathname: '/users', search: location.search }, { replace: true })} />
      <Toast toast={toast} hideClose />
      <OTPModal show={showOTP} onHide={() => setShowOTP(false)} onConfirm={onConfirmOTP} />

      <ConfirmationModal title='Demote User Access' inputConfirm show={showDemote} onHide={() => setShowDemote(false)} onConfirm={() => onSaveRole(null, true)} confirmText='Accept'>
        <p>Are you sure you want to continue with this action?</p>
        <p>You are demoting your current level of access and won't be able to upgrade your access after the change.</p>
      </ConfirmationModal>
    </section>
  );
}
